import {
  DEMO_ADMIN,
  DEMO_COUPONS,
  DEMO_PRODUCTS,
  DEMO_SAMPLE_ORDERS,
  DEMO_SAMPLE_REVIEWS,
  DEMO_STATS,
  now,
} from "@/data/demoData";

const STORAGE = {
  products: "demo_products",
  orders: "demo_orders",
  reviews: "demo_reviews",
  coupons: "demo_coupons",
  addresses: "demo_addresses",
  users: "demo_users",
};

const delay = (ms = 120) => new Promise((r) => setTimeout(r, ms));

function readJson(key, fallback) {
  try {
    const raw = sessionStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
}

function writeJson(key, value) {
  sessionStorage.setItem(key, JSON.stringify(value));
}

function uuid() {
  return crypto.randomUUID?.() || `demo-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function err(status, detail) {
  const e = new Error(detail);
  e.response = { status, data: { detail } };
  return e;
}

function getToken() {
  return localStorage.getItem("atelier_token");
}

function getCurrentUser() {
  const token = getToken();
  if (!token) return null;
  const users = readJson(STORAGE.users, {});
  return users[token] || null;
}

function saveUserSession(token, user) {
  const users = readJson(STORAGE.users, {});
  users[token] = user;
  writeJson(STORAGE.users, users);
}

function getProducts() {
  return readJson(STORAGE.products, DEMO_PRODUCTS);
}

function saveProducts(products) {
  writeJson(STORAGE.products, products);
}

function getOrders() {
  return readJson(STORAGE.orders, DEMO_SAMPLE_ORDERS);
}

function saveOrders(orders) {
  writeJson(STORAGE.orders, orders);
}

function getReviews() {
  return readJson(STORAGE.reviews, DEMO_SAMPLE_REVIEWS);
}

function saveReviews(reviews) {
  writeJson(STORAGE.reviews, reviews);
}

function getCoupons() {
  return readJson(STORAGE.coupons, DEMO_COUPONS);
}

function saveCoupons(coupons) {
  writeJson(STORAGE.coupons, coupons);
}

function getAddresses(userId) {
  const all = readJson(STORAGE.addresses, {});
  return all[userId] || [];
}

function saveAddresses(userId, list) {
  const all = readJson(STORAGE.addresses, {});
  all[userId] = list;
  writeJson(STORAGE.addresses, all);
}

function requireUser() {
  const user = getCurrentUser();
  if (!user) throw err(401, "Missing auth token");
  return user;
}

function requireAdmin() {
  const user = requireUser();
  if (!user.is_admin) throw err(403, "Admin access required");
  return user;
}

function matchPath(pattern, url) {
  const patternParts = pattern.split("/").filter(Boolean);
  const urlParts = url.split("/").filter(Boolean);
  if (patternParts.length !== urlParts.length) return null;
  const params = {};
  for (let i = 0; i < patternParts.length; i++) {
    if (patternParts[i].startsWith(":")) {
      params[patternParts[i].slice(1)] = decodeURIComponent(urlParts[i]);
    } else if (patternParts[i] !== urlParts[i]) {
      return null;
    }
  }
  return params;
}

function computeAmount(items) {
  const products = getProducts();
  const pmap = Object.fromEntries(products.map((p) => [p.id, p]));
  let total = 0;
  const orderItems = [];
  for (const it of items) {
    const p = pmap[it.product_id];
    if (!p) throw err(400, `Product not found: ${it.product_id}`);
    const price = Number(p.price) * (1 - Number(p.discount_percent || 0) / 100);
    const lineTotal = price * it.quantity;
    total += lineTotal;
    orderItems.push({
      product_id: p.id,
      name: p.name,
      image: (p.images || [""])[0],
      unit_price: Math.round(price * 100) / 100,
      quantity: it.quantity,
      size: it.size || null,
      color: it.color || null,
      line_total: Math.round(lineTotal * 100) / 100,
    });
  }
  return { subtotal: Math.round(total * 100) / 100, orderItems };
}

function applyCouponLogic(code, subtotal) {
  const coupon = getCoupons().find((c) => c.code === code.toUpperCase() && c.active);
  if (!coupon) throw err(404, "Invalid or inactive coupon");
  if (subtotal < coupon.min_order) {
    throw err(400, `Minimum order ₹${Math.round(coupon.min_order)} required for this coupon`);
  }
  const discount =
    coupon.kind === "percent"
      ? Math.round(subtotal * (coupon.value / 100) * 100) / 100
      : Math.round(Math.min(coupon.value, subtotal) * 100) / 100;
  return { code: coupon.code, kind: coupon.kind, value: coupon.value, discount };
}

async function handleRequest(method, url, data, config = {}) {
  await delay();
  const path = url.replace(/^\//, "");
  const params = config.params || {};

  // Auth
  if (method === "post" && path === "auth/signup") {
    const email = data.email.toLowerCase();
    if (email === DEMO_ADMIN.email) throw err(400, "Email already registered");
    const user = {
      id: uuid(),
      name: data.name.trim(),
      email,
      is_admin: false,
    };
    const token = `demo_${uuid()}`;
    saveUserSession(token, user);
    return { token, user };
  }

  if (method === "post" && path === "auth/login") {
    const email = data.email.toLowerCase();
    if (email === DEMO_ADMIN.email && data.password === DEMO_ADMIN.password) {
      const user = {
        id: "admin-demo-0001",
        name: "shopnest Admin",
        email: DEMO_ADMIN.email,
        is_admin: true,
      };
      const token = `demo_admin_${uuid()}`;
      saveUserSession(token, user);
      return { token, user };
    }
    const token = getToken();
    const users = readJson(STORAGE.users, {});
    const existing = Object.values(users).find((u) => u.email === email);
    if (existing) {
      const t = token && users[token]?.email === email ? token : `demo_${uuid()}`;
      saveUserSession(t, existing);
      return { token: t, user: existing };
    }
    throw err(401, "Invalid email or password");
  }

  if (method === "get" && path === "auth/me") {
    return requireUser();
  }

  // Products
  if (method === "get" && path === "products") {
    let list = [...getProducts()];
    if (params.category) list = list.filter((p) => p.category === params.category);
    if (params.featured !== undefined) {
      const featured = params.featured === true || params.featured === "true";
      list = list.filter((p) => p.featured === featured);
    }
    if (params.q) {
      const q = params.q.toLowerCase();
      list = list.filter((p) => p.name.toLowerCase().includes(q));
    }
    return list.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  }

  let m = matchPath("products/:id", path);
  if (method === "get" && m) {
    const p = getProducts().find((x) => x.id === m.id);
    if (!p) throw err(404, "Product not found");
    return p;
  }

  if (method === "post" && path === "products") {
    requireAdmin();
    const doc = { id: uuid(), created_at: now(), ...data };
    const products = getProducts();
    products.unshift(doc);
    saveProducts(products);
    return doc;
  }

  if (method === "put" && m) {
    requireAdmin();
    const products = getProducts();
    const idx = products.findIndex((x) => x.id === m.id);
    if (idx < 0) throw err(404, "Product not found");
    products[idx] = { ...products[idx], ...data, id: m.id };
    saveProducts(products);
    return products[idx];
  }

  if (method === "delete" && m) {
    requireAdmin();
    const products = getProducts().filter((x) => x.id !== m.id);
    if (products.length === getProducts().length) throw err(404, "Product not found");
    saveProducts(products);
    return { ok: true };
  }

  if (method === "get" && path === "categories") {
    return [...new Set(getProducts().map((p) => p.category))];
  }

  // Image upload (demo: object URL from file)
  if (method === "post" && path === "uploads/image") {
    requireAdmin();
    const file = data instanceof FormData ? data.get("file") : null;
    if (!file || typeof file === "string") throw err(400, "No file provided");
    const objectUrl = URL.createObjectURL(file);
    return { id: uuid(), url: objectUrl };
  }

  // Reviews
  m = matchPath("reviews/:product_id", path);
  if (method === "get" && m) {
    const docs = getReviews().filter((r) => r.product_id === m.product_id);
    if (!docs.length) return { reviews: [], average: 0, count: 0 };
    const total = docs.reduce((s, d) => s + Number(d.rating), 0);
    return { reviews: docs, average: Math.round((total / docs.length) * 10) / 10, count: docs.length };
  }

  if (method === "post" && path === "reviews") {
    const user = requireUser();
    const reviews = getReviews();
    const existing = reviews.find((r) => r.product_id === data.product_id && r.user_id === user.id);
    const doc = {
      id: existing?.id || uuid(),
      product_id: data.product_id,
      user_id: user.id,
      user_name: user.name,
      rating: data.rating,
      title: (data.title || "").trim(),
      body: (data.body || "").trim(),
      created_at: now(),
    };
    const next = existing
      ? reviews.map((r) => (r.id === existing.id ? doc : r))
      : [doc, ...reviews];
    saveReviews(next);
    return doc;
  }

  // Addresses
  if (method === "get" && path === "addresses") {
    const user = requireUser();
    return getAddresses(user.id);
  }

  if (method === "post" && path === "addresses") {
    const user = requireUser();
    const list = getAddresses(user.id);
    const doc = { id: uuid(), ...data, created_at: now() };
    saveAddresses(user.id, [doc, ...list]);
    return doc;
  }

  m = matchPath("addresses/:addr_id", path);
  if (method === "put" && m) {
    const user = requireUser();
    const list = getAddresses(user.id);
    const idx = list.findIndex((a) => a.id === m.addr_id);
    if (idx < 0) throw err(404, "Address not found");
    list[idx] = { ...list[idx], ...data, id: m.addr_id };
    saveAddresses(user.id, list);
    return list[idx];
  }

  if (method === "delete" && m) {
    const user = requireUser();
    const list = getAddresses(user.id).filter((a) => a.id !== m.addr_id);
    if (list.length === getAddresses(user.id).length) throw err(404, "Address not found");
    saveAddresses(user.id, list);
    return { ok: true };
  }

  // Coupons
  if (method === "get" && path === "coupons") {
    requireAdmin();
    return getCoupons();
  }

  if (method === "post" && path === "coupons") {
    requireAdmin();
    const code = data.code.trim().toUpperCase();
    if (!code) throw err(400, "Code is required");
    if (getCoupons().some((c) => c.code === code)) throw err(400, "Coupon code already exists");
    const doc = {
      id: uuid(),
      code,
      kind: data.kind,
      value: Number(data.value),
      min_order: Number(data.min_order),
      active: data.active !== false,
      created_at: now(),
    };
    const coupons = getCoupons();
    coupons.unshift(doc);
    saveCoupons(coupons);
    return doc;
  }

  m = matchPath("coupons/:coupon_id", path);
  if (method === "delete" && m) {
    requireAdmin();
    const coupons = getCoupons().filter((c) => c.id !== m.coupon_id);
    if (coupons.length === getCoupons().length) throw err(404, "Coupon not found");
    saveCoupons(coupons);
    return { ok: true };
  }

  m = matchPath("coupons/apply/:code", path);
  if (method === "get" && m) {
    return applyCouponLogic(m.code, Number(params.subtotal || 0));
  }

  // Orders
  if (method === "post" && path === "orders/create") {
    const user = getCurrentUser();
    if (!user && !data.guest_email) throw err(401, "Please sign in or provide guest email to checkout");
    if (!data.items?.length) throw err(400, "Cart is empty");

    const { subtotal, orderItems } = computeAmount(data.items);
    if (subtotal <= 0) throw err(400, "Order total must be positive");

    let discount = 0;
    let couponCode = null;
    if (data.coupon_code) {
      const applied = applyCouponLogic(data.coupon_code, subtotal);
      discount = applied.discount;
      couponCode = applied.code;
    }

    const total = Math.max(0.01, Math.round((subtotal - discount) * 100) / 100);
    const orderId = uuid();
    const userId = user ? user.id : `guest:${data.guest_email.toLowerCase()}`;
    const userEmail = user ? user.email : data.guest_email.toLowerCase();
    const userName = user ? user.name : data.guest_name || data.shipping?.full_name || userEmail.split("@")[0];

    const orderDoc = {
      id: orderId,
      user_id: userId,
      user_email: userEmail,
      is_guest: !user,
      items: orderItems,
      shipping: data.shipping,
      subtotal,
      coupon_code: couponCode,
      discount,
      amount: total,
      amount_paise: Math.round(total * 100),
      currency: "INR",
      razorpay_order_id: `mock_order_${orderId.slice(0, 8)}`,
      razorpay_payment_id: null,
      status: "created",
      created_at: now(),
      mock: true,
    };

    const orders = getOrders();
    orders.unshift(orderDoc);
    saveOrders(orders);

    return {
      order_id: orderId,
      razorpay_order_id: orderDoc.razorpay_order_id,
      amount_paise: orderDoc.amount_paise,
      currency: "INR",
      razorpay_key_id: "",
      mock: true,
      user_name: userName,
      user_email: userEmail,
      subtotal,
      discount,
      amount: total,
    };
  }

  if (method === "post" && path === "orders/verify") {
    const orders = getOrders();
    const idx = orders.findIndex((o) => o.id === data.order_id);
    if (idx < 0) throw err(404, "Order not found");
    orders[idx] = {
      ...orders[idx],
      status: "paid",
      razorpay_payment_id: data.razorpay_payment_id || `mock_pay_${data.order_id.slice(0, 8)}`,
      paid_at: now(),
    };
    saveOrders(orders);
    return { ok: true, status: "paid", mock: true };
  }

  if (method === "get" && path === "orders/mine") {
    const user = requireUser();
    return getOrders().filter((o) => o.user_id === user.id);
  }

  m = matchPath("orders/:order_id", path);
  if (method === "get" && m) {
    const order = getOrders().find((o) => o.id === m.order_id);
    if (!order) throw err(404, "Order not found");
    const user = getCurrentUser();
    if (!order.is_guest) {
      if (!user) throw err(401, "Sign in required");
      if (order.user_id !== user.id && !user.is_admin) throw err(403, "Forbidden");
    }
    return order;
  }

  // Admin
  if (method === "get" && path === "admin/orders") {
    requireAdmin();
    return getOrders();
  }

  m = matchPath("admin/orders/:order_id/status", path);
  if (method === "put" && m) {
    requireAdmin();
    const allowed = new Set(["created", "paid", "shipped", "delivered", "cancelled", "failed"]);
    if (!allowed.has(data.status)) throw err(400, "Invalid status");
    const orders = getOrders();
    const idx = orders.findIndex((o) => o.id === m.order_id);
    if (idx < 0) throw err(404, "Order not found");
    orders[idx] = { ...orders[idx], status: data.status };
    saveOrders(orders);
    return { ok: true };
  }

  if (method === "get" && path === "admin/stats") {
    requireAdmin();
    const products = getProducts();
    const orders = getOrders();
    const paid = orders.filter((o) => ["paid", "shipped", "delivered"].includes(o.status));
    return {
      total_products: products.length,
      total_users: DEMO_STATS.total_users,
      total_orders: orders.length,
      paid_orders: paid.length,
      revenue: paid.reduce((s, o) => s + Number(o.amount || 0), 0),
      mock_payment_mode: true,
    };
  }

  if (method === "get" && path === "") {
    return { message: "shopnest API", mock_payment_mode: true };
  }

  throw err(404, `Demo API: route not found — ${method.toUpperCase()} /${path}`);
}

function wrap(method) {
  return (url, data, config) =>
    handleRequest(method, url, data, config)
      .then((result) => ({ data: result }))
      .catch((e) => Promise.reject(e));
}

export function createMockApi() {
  return {
    get: (url, config) => wrap("get")(url, undefined, config),
    post: (url, data, config) => wrap("post")(url, data, config),
    put: (url, data, config) => wrap("put")(url, data, config),
    delete: (url, config) => wrap("delete")(url, undefined, config),
    interceptors: {
      request: { use: () => {} },
      response: { use: () => {} },
    },
  };
}
